import { Box, Fade, Paper, PaperProps, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCustomQuestionFieldCreate } from "../../../data/CustomWorkflowQuestion/useCustomQuestionFieldCreate";
import { AddButton } from "../../../shared/FormLayout/IconButtons/AddButton";
import { convertWorkflowQuestionFieldToReusableQuestion } from "../../../shared/helpers/CustomQuestionFieldUtils";
import { ReusableCustomQuestionField } from "../../../shared/interfaces/ReusableCustomQuestionField";
import { CustomQuestionField } from "../../../shared/interfaces/Workflow";
import { CustomQuestionBuilderDialog } from "../../WorkflowDetail/CustomQuestionBuilder/CustomQuestionBuilderDialog";
import { ReusableCustomQuestionCard } from "./ReusableCustomQuestionCard";

interface Props {
  customQuestionFields: ReusableCustomQuestionField[] | undefined;
  isLoading?: boolean;
  customerId: number;
  onSuccess?: (successMessage: string) => void;
  onError?: (errorMessage: string) => void;
  displayMode: "view" | "edit" | "select";
  containerProps?: PaperProps;
  selectedQuestions?: ReusableCustomQuestionField[];
  canEditWorkflow?: boolean;
  onSelectionChange?: (selectedQuestionID: ReusableCustomQuestionField) => void;
}

export function ReusableCustomQuestionFields(props: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    customQuestionFields,
    isLoading,
    customerId,
    onSuccess,
    onError,
    displayMode,
    containerProps,
    selectedQuestions,
    canEditWorkflow,
    onSelectionChange,
  } = props;

  const [questionBuilderDialogOpen, setQuestionBuilderDialogOpen] = useState(false);

  const customQuestionFieldCreate = useCustomQuestionFieldCreate({
    onSuccess: handleCreateCustomQuestionFieldSuccess,
    onError: handleCreateCustomQuestionFieldError,
  });

  function handleCreateCustomQuestionFieldSuccess() {
    onSuccess?.(
      t("customQuestionLibrary.libraryItemAdded", {
        val: t("customQuestions.customQuestion"),
      }),
    );
    setQuestionBuilderDialogOpen(false);
  }

  function handleCreateCustomQuestionFieldError() {
    onError?.(t("common.dataCreateError", { val: t("customQuestions.customQuestion") }));
  }

  function handleCreateCustomQuestionClick(customQuestion: CustomQuestionField) {
    const newReusableCustomQuestionField = convertWorkflowQuestionFieldToReusableQuestion(customQuestion, customerId);

    customQuestionFieldCreate.mutate(newReusableCustomQuestionField);
  }

  if (isLoading)
    return (
      <>
        <Fade
          in={isLoading}
          style={{
            transitionDelay: isLoading ? "800ms" : "0ms",
          }}
        >
          <Stack spacing={2} margin={2}>
            {[...Array(6).keys()].map((i) => (
              <Skeleton key={`skeleton-row-${i}`} variant="rectangular" height={80} />
            ))}
          </Stack>
        </Fade>
      </>
    );

  return (
    <Stack>
      {displayMode === "edit" && canEditWorkflow && (
        <Box display="flex" justifyContent="center">
          <AddButton
            onClick={() => setQuestionBuilderDialogOpen(true)}
            tooltip={t("customQuestionLibrary.addQuestionToLibrary")}
          />
        </Box>
      )}
      <Paper variant="outlined" sx={{ p: 1, backgroundColor: theme.palette.lightBackground.main }} {...containerProps}>
        {customQuestionFields && customQuestionFields.length > 0 ? (
          <Stack spacing={1} alignItems="center">
            {customQuestionFields.map((questionField) => {
              return (
                <ReusableCustomQuestionCard
                  key={`reusable-question-field-${questionField.id}`}
                  customerId={customerId}
                  customQuestionField={questionField}
                  displayMode={displayMode}
                  onSuccess={onSuccess}
                  onError={onError}
                  isSelected={selectedQuestions?.some((selectedQuestion) => selectedQuestion.id === questionField.id)}
                  onSelectionChange={onSelectionChange}
                  canEditWorkflow={canEditWorkflow}
                />
              );
            })}
          </Stack>
        ) : (
          <Stack alignItems="center">
            <Typography variant="caption" noWrap>
              {t("customQuestions.customQuestionsEmpty")}
            </Typography>
          </Stack>
        )}
      </Paper>
      {questionBuilderDialogOpen && (
        <CustomQuestionBuilderDialog
          open={questionBuilderDialogOpen}
          onConfirm={handleCreateCustomQuestionClick}
          onCancel={() => setQuestionBuilderDialogOpen(false)}
        />
      )}
    </Stack>
  );
}
